// Port of webapp/src/components/table/table.tsx (+ tableHeaders/tableRows).
//
// Deviations, deliberate and documented in the README:
//  - cells open the Card Detail sheet instead of mounting the full inline
//    property editors upstream uses per column type;
//  - no calculation row (upstream's per-column count/sum) and no grouping
//    subtotals.

import {MouseEvent as ReactMouseEvent, ReactElement, useEffect, useMemo, useRef, useState} from 'react'
import classnames from 'classnames'
import {Board, IPropertyTemplate} from '../../types/board'
import {BoardView} from '../../types/boardView'
import {Card} from '../../types/card'
import {IUser} from '../../types/user'
import {Constants} from '../../common/constants'
import * as mutator from '../../state/mutator'
import {Icon} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem, MenuSeparator} from '../common/menu'
import PropertyValue from '../propertyValue'
import {groupCardsByOptions} from '../../state/boardUtils'

type Column = {id: string, template: IPropertyTemplate, width: number}

type Props = {
    board: Board
    view: BoardView
    cards: Card[]
    usersById: Record<string, IUser>
    readOnly: boolean
    onCardClicked: (cardId: string) => void
}

const MIN_WIDTH = Constants.minColumnWidth

function columnWidth(view: BoardView, id: string): number {
    return view.fields.columnWidths[id] || (id === Constants.titleColumnId ? Constants.defaultTitleColumnWidth : 180)
}

export default function Table({board, view, cards, usersById, readOnly, onCardClicked}: Props): ReactElement {
    const visibleProps = useMemo(() => {
        const ids = view.fields.visiblePropertyIds
        return ids.length ? board.cardProperties.filter((p) => ids.includes(p.id)) : board.cardProperties
    }, [board.cardProperties, view.fields.visiblePropertyIds])

    const columns: Column[] = [
        {id: Constants.titleColumnId, template: {id: Constants.titleColumnId, name: '标题', type: 'text', options: []}, width: columnWidth(view, Constants.titleColumnId)},
        ...visibleProps.map((p) => ({id: p.id, template: p, width: columnWidth(view, p.id)})),
    ]

    const groupByProperty = view.fields.groupById ? board.cardProperties.find((p) => p.id === view.fields.groupById) : undefined
    const rows: Array<{group: string, cards: Card[]}> = useMemo(() => {
        if (!groupByProperty) {
            return [{group: '', cards}]
        }
        const optionIds = [...groupByProperty.options.map((o) => o.id), '']
        return groupCardsByOptions(cards, optionIds, groupByProperty).map((g) => ({group: g.option.value, cards: g.cards}))
    }, [cards, groupByProperty])

    const sortFor = (id: string): boolean | undefined => {
        const option = view.fields.sortOptions.find((o) => o.propertyId === id)
        return option ? !option.reversed : undefined
    }

    const toggleSort = (id: string): void => {
        const existing = view.fields.sortOptions.find((o) => o.propertyId === id)
        void mutator.changeViewSortBy(board.id, view, id, existing ? !existing.reversed : false)
    }

    const hideColumn = (id: string): void => {
        const ids = columns.map((c) => c.id).filter((c) => c !== Constants.titleColumnId)
        const next = ids.filter((c) => c !== id)
        void mutator.changeViewVisibleProperties(board.id, view, next)
    }

    const showAllColumns = (): void => {
        void mutator.changeViewVisibleProperties(board.id, view, board.cardProperties.map((p) => p.id))
    }

    // ---- column resize ----------------------------------------------------

    const resize = useRef<{id: string, startX: number, startWidth: number, width: number} | null>(null)
    const [liveWidth, setLiveWidth] = useState<{id: string, width: number} | null>(null)

    useEffect(() => {
        const onMouseMove = (event: MouseEvent): void => {
            const state = resize.current
            if (!state) {
                return
            }
            state.width = Math.max(MIN_WIDTH, state.startWidth + (event.clientX - state.startX))
            setLiveWidth({id: state.id, width: state.width})
        }

        const onMouseUp = (): void => {
            const state = resize.current
            if (state) {
                void mutator.changeViewColumnWidth(board.id, view, state.id, state.width)
            }
            resize.current = null
            setLiveWidth(null)
        }

        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)
        return () => {
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
        }
    }, [board.id, view])

    const startResize = (event: ReactMouseEvent, id: string, width: number): void => {
        event.preventDefault()
        event.stopPropagation()
        resize.current = {id, startX: event.clientX, startWidth: width, width}
    }

    const widthOf = (column: Column): number => (liveWidth && liveWidth.id === column.id ? liveWidth.width : column.width)

    return (
        <div className='Table flex-1 overflow-auto'>
            {cards.length === 0 && (
                <div className='px-1 py-6 text-sm opacity-60'>当前视图下没有匹配的卡片。</div>
            )}
            <div className='min-w-full' style={{width: columns.reduce((sum, c) => sum + widthOf(c), 0)}}>
                <div className='octo-table-header sticky top-0 z-10 flex bg-main-bg' role='row'>
                    {columns.map((column) => (
                        <div
                            key={column.id}
                            className='group relative flex items-center gap-2 px-3 py-2 font-semibold'
                            style={{width: widthOf(column), flex: `0 0 ${widthOf(column)}px`}}
                            role='columnheader'
                        >
                            <span className='truncate'>{column.template.name}</span>
                            {sortFor(column.id) !== undefined && (
                                <Icon name={sortFor(column.id) ? 'sort-asc' : 'sort-desc'} size={13} className='shrink-0 opacity-70'/>
                            )}
                            {!readOnly && (
                                <ButtonWithMenu
                                    className='ml-auto opacity-0 group-hover:opacity-100'
                                    button={<span className='IconButton' aria-label='列选项'><Icon name='dots' size={14}/></span>}
                                >
                                    <Menu ariaLabel='列选项'>
                                        <MenuItem icon={sortFor(column.id) ? 'sort-desc' : 'sort-asc'} onClick={() => toggleSort(column.id)}>
                                            {sortFor(column.id) ? '降序排列' : '升序排列'}
                                        </MenuItem>
                                        {column.id !== Constants.titleColumnId && (
                                            <>
                                                <MenuSeparator/>
                                                <MenuItem icon='eye-off' onClick={() => hideColumn(column.id)}>隐藏此列</MenuItem>
                                            </>
                                        )}
                                        {columns.length - 1 < board.cardProperties.length && (
                                            <MenuItem icon='eye' onClick={showAllColumns}>显示所有列</MenuItem>
                                        )}
                                    </Menu>
                                </ButtonWithMenu>
                            )}
                            <span
                                className='absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-button-bg/40'
                                role='separator'
                                aria-orientation='vertical'
                                onMouseDown={(event) => startResize(event, column.id, widthOf(column))}
                            />
                        </div>
                    ))}
                </div>

                {rows.map((row) => (
                    <div key={row.group || 'ungrouped'}>
                        {groupByProperty && (
                            <div className='GroupHeader flex items-center gap-2 px-3 py-1.5 text-sm font-semibold' style={{width: '100%'}}>
                                <Icon name='chevron-down' size={13}/>
                                {row.group || `未设置${groupByProperty.name}`}
                                <span className='opacity-60'>{row.cards.length}</span>
                            </div>
                        )}
                        {row.cards.map((card) => (
                            <div
                                key={card.id}
                                className='octo-table-row flex items-stretch'
                                role='row'
                                onClick={() => onCardClicked(card.id)}
                            >
                                {columns.map((column) => (
                                    <div
                                        key={column.id}
                                        className={classnames('flex items-center overflow-hidden px-3 py-2', column.id === Constants.titleColumnId && 'font-medium')}
                                        style={{width: widthOf(column), flex: `0 0 ${widthOf(column)}px`}}
                                        role='cell'
                                    >
                                        {column.id === Constants.titleColumnId ? (
                                            <span className='flex min-w-0 items-center gap-2'>
                                                {card.fields.icon && <span aria-hidden={true}>{card.fields.icon}</span>}
                                                <span className='octo-propertyname truncate' title={card.title}>{card.title || '未命名'}</span>
                                            </span>
                                        ) : (
                                            <PropertyValue
                                                template={column.template}
                                                card={card}
                                                usersById={usersById}
                                                asChip={column.template.type === 'select' || column.template.type === 'multiSelect' || column.template.type === 'date'}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )
}
